import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Brain, FileText, TrendingUp, DollarSign, Target, Shield,
  CheckCircle, AlertTriangle, Zap, RefreshCw, Copy, Printer,
  Star, ArrowRight, Building2, Clock, BarChart3
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(n) {
  if (!n && n !== 0) return "$0";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
}

function GradeTag({ grade }) {
  const cfg = {
    "A+": "bg-[#00FF88]/10 border-[#00FF88]/30 text-[#00FF88]",
    "A":  "bg-[#00FF88]/10 border-[#00FF88]/30 text-[#00FF88]",
    "B+": "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]",
    "B":  "bg-[#D4AF37]/10 border-[#D4AF37]/30 text-[#D4AF37]",
    "C":  "bg-[#FF9F0A]/10 border-[#FF9F0A]/30 text-[#FF9F0A]",
  };
  return (
    <span className={`px-3 py-1 border rounded-full font-bold text-sm ${cfg[grade] || cfg["B"]}`}>
      {grade}
    </span>
  );
}

function Stat({ label, value, sub, highlight }) {
  return (
    <div className={`p-4 rounded-lg border ${highlight ? "bg-[#D4AF37]/[0.06] border-[#D4AF37]/20" : "bg-white/[0.02] border-white/[0.06]"}`}>
      <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-xl font-bold font-mono ${highlight ? "text-[#D4AF37]" : "text-white"}`}>{value}</p>
      {sub && <p className="text-[10px] text-white/30 mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ title, icon: Icon, children, accentColor = "text-white/50" }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-xl p-5 space-y-3">
      <p className={`text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 ${accentColor}`}>
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </p>
      {children}
    </div>
  );
}

export default function DealMemoGenerator({ deal }) {
  const [memo, setMemo] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/deals/${deal.deal_id}/generate-memo`,
        {},
        { withCredentials: true }
      );
      setMemo(res.data);
      toast.success("Deal memo generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (!memo) return;
    const text = `
${memo.deal_title}
${memo.tagline}

${memo.executive_summary}

FINANCIALS
Purchase Price: ${fmt(memo.financial_breakdown?.purchase_price)}
Rehab: ${fmt(memo.financial_breakdown?.rehab_estimate)}
Total In: ${fmt(memo.financial_breakdown?.total_in)}
ARV: ${fmt(memo.financial_breakdown?.arv)}
Projected Profit: ${fmt(memo.financial_breakdown?.projected_profit)}
ROI: ${memo.financial_breakdown?.roi_pct}%

INVESTMENT THESIS
${(memo.investment_thesis || []).map((t, i) => `${i + 1}. ${t}`).join("\n")}

EXIT STRATEGY
${memo.exit_strategy?.narrative}

Generated by DealForge™ AI
    `.trim();
    navigator.clipboard.writeText(text);
    toast.success("Memo copied to clipboard");
  };

  const handlePrint = () => window.print();

  // Empty state
  if (!memo) {
    const hasData = deal.purchase_price || deal.arv || deal.rehab_estimate;
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-6" data-testid="deal-memo-generator">
        <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
          <Brain className="w-8 h-8 text-[#D4AF37]" />
        </div>
        <div className="space-y-2 max-w-sm">
          <h3 className="text-white font-bold text-lg">AI Deal Memo</h3>
          <p className="text-white/40 text-sm leading-relaxed">
            Generate a polished investor brief in seconds — perfect for sharing with private lenders, JV partners, or co-investors.
          </p>
        </div>
        {!hasData && (
          <div className="px-4 py-3 bg-amber-500/[0.06] border border-amber-500/20 rounded-lg max-w-sm">
            <p className="text-[#FF9F0A] text-xs font-semibold mb-1">Add deal financials first</p>
            <p className="text-white/40 text-xs">Fill in purchase price, ARV, and rehab estimate in the Calculator tab for the best memo quality.</p>
          </div>
        )}
        <div className="grid grid-cols-3 gap-3 max-w-sm w-full text-left">
          {[
            { icon: FileText, text: "Executive summary & investment thesis" },
            { icon: BarChart3, text: "Full financial breakdown with ROI" },
            { icon: Shield, text: "Risk analysis & exit strategy" },
          ].map((item, i) => (
            <div key={i} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 text-center space-y-2">
              <item.icon className="w-5 h-5 text-[#D4AF37] mx-auto" />
              <p className="text-white/50 text-[11px]">{item.text}</p>
            </div>
          ))}
        </div>
        <button
          onClick={generate}
          disabled={loading}
          data-testid="generate-memo-btn"
          className="flex items-center gap-2 px-8 py-3 bg-gradient-to-br from-[#D4AF37] to-[#AA8C2C] text-black font-bold rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
        >
          {loading ? (
            <><RefreshCw className="w-4 h-4 animate-spin" /> Generating...</>
          ) : (
            <><Brain className="w-4 h-4" /> Generate AI Deal Memo</>
          )}
        </button>
        <p className="text-white/20 text-xs">Powered by Llama 3.3 70B</p>
      </div>
    );
  }

  return (
    <div className="space-y-5 print:space-y-4" data-testid="deal-memo-result">
      {/* Toolbar */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg flex items-center justify-center">
            <Brain className="w-4 h-4 text-[#D4AF37]" />
          </div>
          <span className="text-white/60 text-sm">AI Deal Memo</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={generate}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-white/[0.08] text-white/50 hover:text-white hover:border-white/20 rounded-lg text-xs transition-all"
            data-testid="regenerate-memo-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Regenerate
          </button>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-white/[0.08] text-white/50 hover:text-white hover:border-white/20 rounded-lg text-xs transition-all"
            data-testid="copy-memo-btn"
          >
            <Copy className="w-3.5 h-3.5" />
            Copy
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-white/[0.08] text-white/50 hover:text-white hover:border-white/20 rounded-lg text-xs transition-all"
            data-testid="print-memo-btn"
          >
            <Printer className="w-3.5 h-3.5" />
            Print
          </button>
        </div>
      </div>

      {/* Header Card */}
      <div className="bg-gradient-to-br from-[#111] to-[#0A0A0A] border border-white/[0.08] rounded-xl p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] px-2 py-0.5 bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] rounded-full uppercase tracking-wider font-semibold">
                DealForge™ Deal Memo
              </span>
              {memo.deal_grade && <GradeTag grade={memo.deal_grade} />}
            </div>
            <h2 className="text-white font-bold text-xl mb-1">{memo.deal_title}</h2>
            <p className="text-white/50 text-sm">{memo.tagline}</p>
          </div>
          {memo.confidence_score && (
            <div className="text-center flex-shrink-0">
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-1">Confidence</p>
              <p className="text-3xl font-bold text-[#D4AF37]">{memo.confidence_score}</p>
              <p className="text-white/30 text-[10px]">/ 100</p>
            </div>
          )}
        </div>
        {memo.executive_summary && (
          <p className="text-white/60 text-sm leading-relaxed mt-4 pt-4 border-t border-white/[0.06]">
            {memo.executive_summary}
          </p>
        )}
      </div>

      {/* Deal Snapshot Row */}
      {memo.deal_snapshot && (
        <div className="grid grid-cols-4 gap-3">
          <Stat label="Type" value={memo.deal_snapshot.type || memo.input_data?.property_address?.split(",")[1]?.trim() || "—"} />
          <Stat label="Location" value={memo.deal_snapshot.location?.split(",").slice(-2).join(", ") || "—"} />
          <Stat label="Stage" value={memo.deal_snapshot.stage || "—"} />
          <Stat label="Timeline" value={memo.deal_snapshot.timeline || "—"} />
        </div>
      )}

      {/* Financial Breakdown */}
      {memo.financial_breakdown && (
        <Section title="Financial Breakdown" icon={DollarSign} accentColor="text-[#D4AF37]/60">
          <div className="grid grid-cols-4 gap-3">
            <Stat label="Purchase Price" value={fmt(memo.financial_breakdown.purchase_price)} />
            <Stat label="Rehab Estimate" value={fmt(memo.financial_breakdown.rehab_estimate)} />
            <Stat label="Total In" value={fmt(memo.financial_breakdown.total_in)} />
            <Stat label="ARV" value={fmt(memo.financial_breakdown.arv)} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-1">
            <Stat label="Projected Profit" value={fmt(memo.financial_breakdown.projected_profit)} highlight />
            <Stat label="ROI" value={`${memo.financial_breakdown.roi_pct}%`} sub="Return on total investment" highlight />
          </div>
          {memo.financial_breakdown.narrative && (
            <p className="text-white/50 text-sm leading-relaxed">{memo.financial_breakdown.narrative}</p>
          )}
        </Section>
      )}

      {/* Investment Thesis + Value Add */}
      <div className="grid grid-cols-2 gap-4">
        {memo.investment_thesis?.length > 0 && (
          <Section title="Investment Thesis" icon={TrendingUp} accentColor="text-[#00FF88]/60">
            <ul className="space-y-2">
              {memo.investment_thesis.map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                  <CheckCircle className="w-3.5 h-3.5 text-[#00FF88] mt-0.5 flex-shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </Section>
        )}
        {memo.value_add_opportunities?.length > 0 && (
          <Section title="Value-Add Opportunities" icon={Zap} accentColor="text-[#BF5AF2]/60">
            <ul className="space-y-2">
              {memo.value_add_opportunities.map((v, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                  <Zap className="w-3.5 h-3.5 text-[#BF5AF2] mt-0.5 flex-shrink-0" />
                  {v}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {/* Risk Factors */}
      {memo.risk_factors?.length > 0 && (
        <Section title="Risk Factors" icon={Shield} accentColor="text-[#FF9F0A]/60">
          <div className="space-y-3">
            {memo.risk_factors.map((r, i) => (
              <div key={i} className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-[#FF9F0A] flex-shrink-0" />
                  <span className="text-white/70 text-sm font-medium">{r.risk}</span>
                </div>
                {r.mitigation && (
                  <p className="text-white/30 text-xs pl-6">→ {r.mitigation}</p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Exit Strategy + Capital Structure */}
      <div className="grid grid-cols-2 gap-4">
        {memo.exit_strategy && (
          <Section title="Exit Strategy" icon={Target} accentColor="text-[#D4AF37]/60">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <ArrowRight className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="text-white/70 text-sm font-medium">{memo.exit_strategy.primary}</span>
              </div>
              {memo.exit_strategy.secondary && (
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-3.5 h-3.5 text-white/20" />
                  <span className="text-white/40 text-sm">Backup: {memo.exit_strategy.secondary}</span>
                </div>
              )}
              {memo.exit_strategy.timeline_months && (
                <div className="flex items-center gap-2 mt-1">
                  <Clock className="w-3.5 h-3.5 text-white/30" />
                  <span className="text-white/40 text-xs">{memo.exit_strategy.timeline_months} month timeline</span>
                </div>
              )}
            </div>
            {memo.exit_strategy.narrative && (
              <p className="text-white/40 text-xs leading-relaxed">{memo.exit_strategy.narrative}</p>
            )}
          </Section>
        )}
        {memo.capital_structure && (
          <Section title="Capital Structure" icon={Building2} accentColor="text-white/30">
            <div className="space-y-2">
              {memo.capital_structure.equity_needed > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-white/40 text-xs">Equity Needed</span>
                  <span className="text-white/70 text-sm font-mono font-bold">{fmt(memo.capital_structure.equity_needed)}</span>
                </div>
              )}
              {memo.capital_structure.debt_available > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-white/40 text-xs">Debt Available</span>
                  <span className="text-white/70 text-sm font-mono font-bold">{fmt(memo.capital_structure.debt_available)}</span>
                </div>
              )}
              {memo.capital_structure.suggested_structure && (
                <p className="text-white/40 text-xs pt-2 border-t border-white/[0.04]">{memo.capital_structure.suggested_structure}</p>
              )}
            </div>
          </Section>
        )}
      </div>

      {/* Next Steps */}
      {memo.next_steps?.length > 0 && (
        <Section title="Next Steps" icon={CheckCircle} accentColor="text-[#00FF88]/60">
          <ol className="space-y-2">
            {memo.next_steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-white/60">
                <span className="w-5 h-5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 text-[#D4AF37] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </Section>
      )}

      {/* Footer */}
      <p className="text-white/20 text-[10px] text-center border-t border-white/[0.04] pt-4">
        Generated by DealForge™ AI · {memo.model} · For informational purposes only. Not financial advice.
      </p>
    </div>
  );
}
